import Swal from "sweetalert2";

/* ----------------------------------------------------
   1. Fallback hours (your original MST version)
---------------------------------------------------- */
const FALLBACK_MST_HOURS = [
  { day: 0, start: "11:00", end: "20:00" }, // Sunday
  { day: 1, start: "11:00", end: "21:00" }, // Monday
  { day: 2, start: "11:00", end: "21:00" }, // Tuesday
  { day: 3, start: "11:00", end: "21:00" }, // Wednesday
  { day: 4, start: "11:00", end: "21:00" }, // Thursday
  { day: 5, start: "11:00", end: "21:00" }, // Friday
  { day: 6, start: "11:00", end: "21:00" }, // Saturday
];

const TIMEZONE_MAP = {
  "MST": "America/Denver",
  "PST": "America/Los_Angeles",
  "EST": "America/New_York",
  "CST": "America/Chicago",
  // Add more mappings as needed
};

/* ----------------------------------------------------
   2. API Aware Store-Open Logic
---------------------------------------------------- */
export const isStoreOpen = (hoursFromAPI) => {
  // If API hours exist → use full dynamic logic
  if (hoursFromAPI && hoursFromAPI.time_zone) {
    return isStoreOpenAPI(hoursFromAPI);
  }

  // Otherwise → fallback to your old MST logic
  return isStoreOpenFallback();
};

/* ----------------------------------------------------
   3. Full API logic
---------------------------------------------------- */
const isStoreOpenAPI = (hours) => {
  const timeZone = TIMEZONE_MAP[hours.time_zone] || "America/Denver";
  const now = new Date();

  // Day name: "monday", "tuesday", etc.
  const currentDay = now
    .toLocaleString("en-US", { timeZone, weekday: "long" })
    .toLowerCase();

  // YYYY-MM-DD in the restaurant's timezone
  const currentDate = new Date().toLocaleDateString("en-CA", { timeZone });

  // Check special hours
  const special = hours.specialhours?.find((d) => d.date === currentDate);

  const currentTimeStr = now.toLocaleTimeString("en-US", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });

  const getMinutes = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  const nowMin = getMinutes(currentTimeStr);

  /* -------------------------------
     SPECIAL HOURS OVERRIDE
  -------------------------------- */
  if (special) {
    if (special.is_closed) return false;

    if (special.open_time && special.close_time) {
      const openMin = getMinutes(special.open_time);
      const closeMin = getMinutes(special.close_time);
      return nowMin >= openMin && nowMin <= closeMin;
    }
    // Else → fall back to regular hours
  }

  /* -------------------------------
     REGULAR WEEKLY HOURS
  -------------------------------- */
  const todayRaw = hours[currentDay];
  if (!todayRaw || todayRaw.toLowerCase() === "closed") return false;

  const [mainRange, breakRange] = todayRaw.split("|");
  const [openStr, closeStr] = mainRange.split("-");
  const openMin = getMinutes(openStr);
  const closeMin = getMinutes(closeStr);

  // Outside main open-close
  if (nowMin < openMin || nowMin > closeMin) return false;

  // Break time check
  if (breakRange) {
    const [breakStart, breakEnd] = breakRange.split("-");
    const breakStartMin = getMinutes(breakStart);
    const breakEndMin = getMinutes(breakEnd);

    if (nowMin >= breakStartMin && nowMin < breakEndMin) return false;
  }

  return true;
};

/* ----------------------------------------------------
   4. Fallback MST logic (your old version)
---------------------------------------------------- */
const isStoreOpenFallback = () => {
  const nowStr = new Date().toLocaleString("en-US", {
    timeZone: "America/Denver",
  });

  const now = new Date(nowStr);
  const currentDay = now.getDay();

  const currentTime = now.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Denver",
  });

  const todayHours = FALLBACK_MST_HOURS.filter(
    (h) => h.day === currentDay
  );

  if (!todayHours.length) return false;

  const current = new Date(`1970-01-01T${currentTime}:00`);

  return todayHours.some((h) => {
    const start = new Date(`1970-01-01T${h.start}:00`);
    const end = new Date(`1970-01-01T${h.end}:00`);
    return current >= start && current <= end;
  });
};

/* ----------------------------------------------------
   5. Updated Error Modal (API-first)
---------------------------------------------------- */
export const showTimeError = (hoursFromAPI) => {
  // If API hours exist → show dynamic modal
  if (hoursFromAPI && hoursFromAPI.time_zone) {
    return showTimeErrorAPI(hoursFromAPI);
  }

  // Otherwise fallback to your old MST message
  return Swal.fire({
    icon: "error",
    title: "Please come back later.",
    text: "Our store is currently closed. Please check our operating hours and visit us during those times.",});
};

/* ----------------------------------------------------
   6. API dynamic version of the alert
---------------------------------------------------- */
const showTimeErrorAPI = (hours) => {
  const timeZone = hours.time_zone;
  const days = [
    "Monday", "Tuesday", "Wednesday", "Thursday",
    "Friday", "Saturday", "Sunday",
  ];

  const formatted = days
    .map((day) => {
      const key = day.toLowerCase();
      const raw = hours[key];

      if (!raw || raw.toLowerCase() === "closed") return `${day}: Closed`;

      const [main, breaks] = raw.split("|");
      return `${day}: ${main}${breaks ? ` (Break: ${breaks})` : ""}`;
    })
    .join("\n");

  Swal.fire({
    icon: "error",
    title: "Please come back later.",
    html: `<p>Our store is currently closed. Please check our operating hours below and visit us during those times.</p>
           <pre style="text-align: left; white-space: pre-wrap; margin-top: 10px;">${formatted}</pre>
           <p style="margin-top: 10px;"><em>All times are in ${timeZone}.</em></p>`,
    confirmButtonText: "OK",
  });
};



export const checkIfLunchTime = () => {
  // Get the current date and time in the Mountain Time Zone (Denver)
  const currentDate = new Date().toLocaleString("en-US", {
    timeZone: "America/Denver",
  });

  const now = new Date(currentDate);
  const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday

  // Hide lunch logic on weekends (Saturday & Sunday)
  if (currentDay === 0 || currentDay === 6) {
    return false;
  }

  // Get current time in HH:mm (24h)
  const currentTime = now.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Denver",
  });

  // Define lunch hours (11:00 AM - 3:00 PM)
  const openingHours = { start: "11:00", end: "15:00" };

  // Parse time strings
  const [currentHour, currentMinute] = currentTime.split(":").map(Number);
  const [startHour, startMinute] = openingHours.start.split(":").map(Number);
  const [endHour, endMinute] = openingHours.end.split(":").map(Number);

  // Check if within lunch window
  const isLunchTime =
    (currentHour > startHour ||
      (currentHour === startHour && currentMinute >= startMinute)) &&
    (currentHour < endHour ||
      (currentHour === endHour && currentMinute <= endMinute));

  return isLunchTime;
};
